<div *ngIf="isLoading == true; else hasLoaded">
  <app-spinner></app-spinner>
</div>
<ng-template #hasLoaded>
  <ng-container class="product-edit" *ngIf="readProductOption">
    <div class="position-relative productForm">
      <!-- Always-visible close button on its own top-right row, so it never
           collides with the header action buttons on any screen size. -->
      <div class="product-edit__close-row">
        <button mat-icon-button type="button" class="product-edit__close-btn"
                (click)="closeDialog()"
                matTooltip="{{ 'close' | translate }}"
                aria-label="Close">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <div class="product-edit__header-row" fxLayout="row" fxLayoutAlign="space-between center" fxLayout.lt-md="column">
        <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="15px" fxLayoutAlign.lt-md="start center">
          <!-- The frame itself is the control: click it, or drop a file on it. -->
          <div class="product-image"
               [class.product-image--empty]="!productImageUrl"
               [class.product-image--dragging]="draggingImage"
               [class.product-image--readonly]="matDialogData.finalized"
               (click)="matDialogData.finalized ? null : productImageInput.click()"
               (dragover)="onImageDragOver($event)"
               (dragleave)="onImageDragLeave($event)"
               (drop)="onImageDrop($event)"
               [matTooltip]="matDialogData.finalized ? '' : ((galleryIsFull ? 'product.image.galleryFull' : (productImageUrl ? 'product.image.addHintDrop' : 'product.image.uploadHint')) | translate: { max: maxProductImages })"
               matTooltipClass="tooltip">

            <img *ngIf="productImageUrl" [src]="productImageUrl" class="product-image__preview" alt="">

            <div *ngIf="!productImageUrl" class="product-image__placeholder">
              <mat-icon>add_a_photo</mat-icon>
              <span>{{ 'product.image.uploadHint' | translate }}</span>
            </div>

            <!-- Hover state, and the same surface while a file is dragged over. -->
            <div class="product-image__overlay" *ngIf="!matDialogData.finalized && !uploadingImage">
              <mat-icon>photo_camera</mat-icon>
            </div>

            <div class="product-image__busy" *ngIf="uploadingImage">
              <mat-spinner diameter="24"></mat-spinner>
            </div>

            <button type="button" class="product-image__remove"
                    *ngIf="productImageUrl && !matDialogData.finalized && !uploadingImage"
                    (click)="$event.stopPropagation(); onRemoveImage()"
                    [matTooltip]="'product.image.remove' | translate"
                    matTooltipClass="tooltip">
              <mat-icon>close</mat-icon>
            </button>

            <input #productImageInput type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple hidden (change)="onImageSelected($event)">
          </div>

          <!-- The rest of the gallery. The frame above always shows the cover;
               these are the other photos, with the add tile last. Clicking a
               thumbnail promotes it to cover, which is also what every product
               list shows. -->
          <div class="product-gallery" *ngIf="!matDialogData.finalized || productImages.length > 1">
            <div class="product-gallery__item"
                 *ngFor="let image of productImages"
                 [class.product-gallery__item--cover]="isCover(image)">
              <img [src]="image.displayThumbUrl" alt=""
                   (click)="matDialogData.finalized ? null : onMakeCover(image)"
                   [matTooltip]="(isCover(image) ? 'product.image.isCover' : 'product.image.makeCover') | translate"
                   matTooltipClass="tooltip">
              <span class="product-gallery__badge" *ngIf="isCover(image)">
                <mat-icon>star</mat-icon>
              </span>
              <button type="button" class="product-gallery__remove"
                      *ngIf="!matDialogData.finalized && !uploadingImage"
                      (click)="$event.stopPropagation(); onRemoveGalleryImage(image)"
                      [matTooltip]="'product.image.remove' | translate"
                      matTooltipClass="tooltip">
                <mat-icon>close</mat-icon>
              </button>
            </div>

            <div class="product-gallery__add"
                 *ngIf="!matDialogData.finalized && !galleryIsFull"
                 (click)="galleryImageInput.click()"
                 [matTooltip]="'product.image.addHint' | translate"
                 matTooltipClass="tooltip">
              <mat-icon>add</mat-icon>
              <span class="product-gallery__count">{{ productImages.length }}/{{ maxProductImages }}</span>
              <input #galleryImageInput type="file" accept="image/*" hidden (change)="onImageSelected($event)">
            </div>

            <div class="product-gallery__full" *ngIf="!matDialogData.finalized && galleryIsFull">
              {{ productImages.length }}/{{ maxProductImages }}
            </div>
          </div>
          <!-- Name and the stock figures stack beside the photo. -->
          <div fxLayout="column" fxLayoutAlign="center start" class="product-edit__title-block">
            <h3><span [innerHTML]="product$?.prdNameEn"></span></h3>
          <!-- Asset: Show borrow status and location instead of on-hand quantities -->
          <div *ngIf="form.get('prdIsAsset')?.value && locUsesAssetModule === 1; else normalOnHandArea" fxLayout="row" class="margin-bottom onhand-data-area asset-info-area" fxLayout.lt-md="column" fxLayoutGap="10px">
            <div class="asset-status-badge" [ngClass]="{'borrowed': assetBorrowInfo?.status === 'borrowed', 'available': assetBorrowInfo?.status === 'available'}">
              <mat-icon class="status-icon">{{ assetBorrowInfo?.status === 'borrowed' ? 'person' : 'check_circle' }}</mat-icon>
              <span *ngIf="assetBorrowInfo?.status === 'borrowed'">{{ 'asset.borrowedBy' | translate }}: {{ assetBorrowInfo?.borrowerName }}</span>
              <span *ngIf="assetBorrowInfo?.status === 'available'">{{ 'asset.available' | translate }}</span>
            </div>
            <div class="asset-location-badge" *ngIf="assetBorrowInfo?.location">
              <mat-icon class="location-icon">place</mat-icon>
              <span>{{ assetBorrowInfo?.location }}</span>
            </div>
          </div>
          </div>
        </div>

        <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="5px" fxLayout.lt-md="row wrap" fxLayoutAlign="center center">
          <button mat-icon-button
                  *ngIf="settings$?.usrMinStockAlertEnabled"
                  (click)="openAlertModal()"
                  matTooltip="Stock Alert Settings"
                  color="primary">
            <mat-icon class="alert-bell" [class.alert-bell--on]="alertEnabled">
              {{ alertEnabled ? 'notifications_active' : 'notifications' }}
            </mat-icon>
          </button>



          <!--<div [hidden]="settings$.locEnablePrinting == 0">
            <button mat-raised-button fxFlexAlign="center" color="accent" type="button" id="print__btn"
            onclick="printSelectedOld()" [disabled]="isPrintDisabled == true || hasPrinter == false">
              <mat-icon aria-hidden="false" aria-label="Example print icon" class="product-edit--button--margin">print</mat-icon>
              <span fxHide.xs>{{ "printLabel" | translate }}</span>
            </button>
            <mat-error *ngIf="hasPrinter == false" class="position-absolute mt-5">
              {{ "error.noDymo" | translate }}
            </mat-error>
          </div>

          <div [hidden]="settings$.locEnablePrinting == 0">
            <button mat-raised-button color="accent" id="print__btn" [matMenuTriggerFor]="menu" [disabled]="hasPrinter == false">
              <i class="fas fa-barcode product-edit--button--margin"></i>
              <span fxHide.xs *ngIf="printLabel == null; else nameBlock">{{ "selectLabel" | translate }}</span>
              <ng-template #nameBlock><span fxHide.xs>{{ printLabel }}</span></ng-template>
            </button>
          </div>-->

          <mat-menu #menu="matMenu" class="product-edit__labelMenu" backdropClass="product-edit__labelMenu__backdrop">
            <button mat-menu-item *ngFor="let b of printList; let i = index" [attr.data-name]="b.name" [attr.data-value]="b.value" (click)="onSelectLabel($event, b.value)">
              {{ b.name }}
            </button>
          </mat-menu>


          <!-- Asset Toggle (locked) - shows asset status, click prevented -->
          <div class="asset-toggle-container locked" *ngIf="locUsesAssetModule === 1 && !matDialogData.finalized && form.get('prdIsAsset')?.value"
               [matTooltip]="'form.product.assetLocked' | translate"
               matTooltipClass="tooltip"
               (click)="$event.stopPropagation(); $event.preventDefault()">
            <mat-slide-toggle [checked]="true"
                              color="primary"
                              class="asset-toggle locked"
                              (click)="$event.stopPropagation(); $event.preventDefault()"
                              (change)="$event.source.checked = true">
              <span class="asset-toggle-label">{{ 'form.product.asset' | translate }}</span>
            </mat-slide-toggle>
          </div>
          <!-- Asset Conversion Warning (inline) -->
          <div class="asset-conversion-warning-inline" *ngIf="assetConversionBlocked">
            <mat-icon class="warning-icon">warning</mat-icon>
            <span>{{ 'asset.activeBorrowsExist' | translate: { count: activeBorrowsForConversion.length } }}</span>
            <button mat-button color="accent" (click)="openAssetBorrowDialog()" class="view-borrows-btn">
              {{ 'asset.viewActiveBorrows' | translate }}
            </button>
          </div>
          <!-- Asset: Borrow/Return button - only if locUsesAssetModule is enabled.
               Shown for asset products regardless of storage selection (an asset can
               be borrowed/returned even when it has no storage placement yet). -->
          <button mat-raised-button color="accent" (click)="openAssetBorrowDialog()" *ngIf="form.get('prdIsAsset')?.value && locUsesAssetModule === 1 && readStorageOption">
              <mat-icon class="product-edit--button--margin">apps</mat-icon>
              <span fxHide.xs>{{ "asset.borrowReturn" | translate }}</span>
            </button>
          <!-- Non-Asset: Product Activity button -->
          <button mat-raised-button color="accent" (click)="openInventoryInformation()" *ngIf="!form.get('prdIsAsset')?.value && showStockInformation && readStorageOption">
              <i class="fas fa-th-list fa-sm product-edit--button--margin"></i>
              <span fxHide.xs>{{ "productActivity" | translate }}</span>
            </button>
          <button mat-raised-button fxFlexAlign="center" color="primary" (click)="onSubmit()" type="submit" [disabled]="!form.valid || (showSerialBlock && serialPatterns$?.length === 0 && form.controls['prdIsSerialized']?.value === true)" *ngIf="!matDialogData.finalized">
            <i class="fas fa-edit fa-sm product-edit--button--margin" fxHide fxShow.xs></i>
            <span fxHide.xs>{{ 'form.product.updateProduct' | translate }}</span>
          </button>

        </div>

      </div>
      <!-- Normal Product: Show total on hand and last counted -->
      <ng-template #normalOnHandArea>
        <div fxLayout="row" class="margin-bottom onhand-data-area" fxLayout.lt-md="column">
          <span class="onhand-text" *ngIf="readStorageOption">{{ "form.product.totalonhand" | translate }} : {{product$.prdTotalOnHand}}</span>
          <span class="onhand-text">{{ "form.product.onhand" | translate }} : {{prdLastCounted}}</span>
          <span class="onhand-text" *ngIf="prdMinQuantity>0">{{ "products.table.minQuantity" | translate }} : {{prdMinQuantity}}</span>
          <span class="stock-status-badge status-low" *ngIf="getStockStatus() === 'low'">{{ 'healthCheck.lowStock' | translate }}</span>
          <span class="stock-status-badge status-out" *ngIf="getStockStatus() === 'out'">{{ 'healthCheck.outOfStock' | translate }}</span>
          <span class="stock-status-badge status-healthy" *ngIf="getStockStatus() === 'healthy'">{{ 'healthCheck.healthyStock' | translate }}</span>
        </div>
      </ng-template>

      <br />

      <form [formGroup]="form">

        <div fxLayout="row" fxLayout.sm="column" fxLayout.xs="column" class="cover product-edit__identity-row">
          <div fxFlex="50" fxFlex.sm="100" fxFlex.xs="100" class="margin-right">
            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label> {{ "productName" | translate }}</mat-label>
              <input matInput required class="form-control" formControlName="prdNameEn" autocomplete="off">
            </mat-form-field>
          </div>

          <!-- Asset Number Input - only for assets, before SKU -->
          <div fxFlex="15" fxFlex.sm="100" fxFlex.xs="100" class="margin-right assets-number-container" *ngIf="locUsesAssetModule === 1 && form.get('prdIsAsset')?.value && !matDialogData.finalized">
            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label>{{ 'form.product.assetNumber' | translate }}</mat-label>
              <input matInput formControlName="prdAssetsNumber" autocomplete="off" maxlength="50" required>
            </mat-form-field>
            <ng-container>
              <mat-error *ngIf="form.controls['prdAssetsNumber'].errors?.required">
                {{ 'form.product.required' | translate }}
              </mat-error>
            </ng-container>
          </div>

          <div fxFlex="20" fxFlex.sm="100" fxFlex.xs="100" class="margin-right">
            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label>SKU</mat-label>
              <input matInput required class="form-control" formControlName="prdCode" (focusout)="onValidatePrdCode($event)" (keyup)="onValidatePrdCode($event)" maxlength="18">
            </mat-form-field>
            <ng-container>
              <mat-error *ngIf="form.controls['prdCode'].errors?.required">
                {{ "form.product.required" | translate }}
              </mat-error>
            </ng-container>
            <ng-container *ngIf="form.controls['prdCode'].errors?.valid == false">
              <mat-error>
                {{ skuError | translate }}
              </mat-error>
            </ng-container>
            <ng-container *ngIf="form.controls['prdCode'].errors?.maxLength">
              <mat-error>
                {{ "form.product.maxLength" | translate }}
              </mat-error>
            </ng-container>
          </div>

          <div fxFlex="30" fxFlex.sm="100" fxFlex.xs="100" class="barcode_edit_box margin-right">
            <mat-form-field  appearance="outline" floatLabel="always">
              <mat-label>{{ "products.table.barcodes" | translate }}</mat-label>
              <!--p *ngIf="barcodes$?.length > 1" class="more__label">
                <span *ngIf="0 > (barcodes$.length - 2); else filledBlock">
                </span>
                <ng-template #filledBlock>+{{ barcodes$?.length - 1 }} {{ "more" | translate }}</ng-template></p-->
              <mat-chip-list #chipList aria-label="Barcode selection" class="product-edit__barcodes MatChipList" fxLayout="row" fxLayoutAlign="start start">
                <mat-chip *ngFor="let b of barcodes$; let i = index" 
                  [selectable]="!matDialogData.finalized"
                  [removable]="!matDialogData.finalized"
                  [ngClass]="b['selected'] == true ? 'MatChip--dark pointer' : 'pointer' " (removed)="onBarcodeDelete(i)" (click)="onSelectBarcode(i)"
                  [attr.data-chipvalue]="i">
                  <span [innerHTML]="b.barcodeData"></span>
                  <button matChipRemove>
                    <mat-icon *ngIf="!matDialogData.finalized">cancel</mat-icon>
                  </button>
                </mat-chip>
                <input placeholder="{{ 'typeNewBarcode' | translate }}" [style.--ph-ch]="('typeNewBarcode' | translate).length" [matChipInputFor]="chipList" matChipInputAddOnBlur="true" id="chipInput" #chipInput
                  [matChipInputSeparatorKeyCodes]="separatorKeysCodes" (matChipInputTokenEnd)="onBarcodeSubmit($event)" (keydown)="onBarcodeKeyDown($event)" (keyup)="onBarcodeKeyUp($event)" [readonly]="matDialogData.finalized">
              </mat-chip-list>
            </mat-form-field>
            <ng-container *ngIf="isBarcodeValid$ == false" class="validation-error">
              <mat-error>
                {{ barcodeError | translate }}
              </mat-error>
            </ng-container>
            <ng-container *ngIf="isBarcodeSingle$==false && multiBarcodeOption==false" class="validation-error">
              <mat-error>
                {{ "barcodeSingleError" | translate }}
              </mat-error>
            </ng-container>
            <!--ng-container>
              <mat-error *ngIf="form.controls['barcodes'].errors?.required">
                {{ "form.product.required" | translate }}
              </mat-error>
            </ng-container-->
          </div>

          <div fxFlex="30" fxFlex.sm="100" fxFlex.xs="100" class="barcode_edit_box" *ngIf="rfidEnabled$ == true">
            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label>{{ "rfidTags" | translate }}</mat-label>
              <mat-chip-list #rfidChipList aria-label="RFID tag list" class="product-edit__barcodes MatChipList" fxLayout="row" fxLayoutAlign="start start">
                <mat-chip *ngFor="let t of rfidTags$; let i = index"
                  [selectable]="false"
                  [removable]="!matDialogData.finalized"
                  class="pointer" (removed)="onRfidDelete(i)">
                  <span [innerHTML]="t.rfidValue"></span>
                  <button matChipRemove>
                    <mat-icon *ngIf="!matDialogData.finalized">cancel</mat-icon>
                  </button>
                </mat-chip>
                <input placeholder="{{ 'typeNewRfidTag' | translate }}" [matChipInputFor]="rfidChipList" matChipInputAddOnBlur="true"
                  (matChipInputTokenEnd)="onRfidSubmit($event)" [readonly]="matDialogData.finalized">
              </mat-chip-list>
            </mat-form-field>
            <ng-container *ngIf="isRfidValid$ == false" class="validation-error">
              <mat-error>
                {{ rfidError }}
              </mat-error>
            </ng-container>
          </div>

        </div>





        <!-- Tags: free text on the product, shared by every location. -->
        <div fxLayout="row" class="cover">
          <div fxFlex="100">
            <app-tags-input [tags]="form.value.prdTags" [disabled]="!!matDialogData?.finalized" (tagsChange)="form.patchValue({ prdTags: $event }); form.markAsDirty()"></app-tags-input>
          </div>
        </div>

        <div fxLayout="row" fxLayout.sm="column" fxLayout.xs="column" class="cover">

          <div [fxFlex]="(settings$.locEnableCustomField1 == 1 || settings$.locEnableCustomField2 == 1) ? '48' : '100'" class="description_box" [ngClass]="{'margin-right': settings$.locEnableCustomField1 == 1 || settings$.locEnableCustomField2 == 1}">
            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label>{{ "productDescription" | translate }}</mat-label>
              <textarea matInput formControlName="prdDescription"></textarea>
            </mat-form-field>
          </div>

          <div [fxFlex]="settings$.locEnableCustomField2 == 1 ? '20' : '52'" fxFlex.sm="100" fxFlex.xs="100" [ngClass]="{'margin-right': settings$.locEnableCustomField2 == 1}" *ngIf="settings$.locEnableCustomField1 == 1">
            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label>{{ locPrdCustomFieldLabel1$ }}</mat-label>
              <input matInput formControlName="prdCustomField1" autocomplete="off" [readonly]="settings$.locLockCustomField1 == 1">
            </mat-form-field>
            <mat-error *ngIf="form.controls.prdCustomField1.errors">{{ "valueLess30" | translate }}</mat-error>
          </div>

          <div [fxFlex]="settings$.locEnableCustomField1 == 1 ? '30' : '52'" fxFlex.sm="100" fxFlex.xs="100" *ngIf="settings$.locEnableCustomField2 == 1">
            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label>{{ locPrdCustomFieldLabel2$ }}</mat-label>
              <input matInput formControlName="prdCustomField2" autocomplete="off" [readonly]="settings$.locLockCustomField2 == 1">
            </mat-form-field>
            <mat-error *ngIf="form.controls.prdCustomField2.errors">{{ "valueLess30" | translate }}</mat-error>
          </div>

        </div>


        <div fxLayout="row" fxLayout.sm="column" fxLayout.xs="column" class="cover">
          <div fxFlex="23" fxFlex.sm="100" fxFlex.xs="100" class="margin-right">
            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label>{{ "form.product.category" | translate }}</mat-label>
              <mat-select formControlName="prdCatID">
                <mat-option [value]="data.prdCatID" *ngFor="let data of categories$ | categoryTree">
                 <span [innerHTML]="data.prdCatPath || data.prdCatNameEn"></span>
                </mat-option>
              </mat-select>
            </mat-form-field>
            <button *ngIf="!matDialogData.finalized" type="button" mat-button mat-raised-button color="accent" (click)="onAddCategory()" class="product-form__categoryBtn" aria-label="Add Button">
              {{ "addCategory" | translate }}
            </button>
          </div>



          <div fxFlex="24" fxFlex.sm="100" fxFlex.xs="100" class="margin-right">
            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label>{{ "form.product.brand" | translate }}</mat-label>
              <input matInput class="form-control" formControlName="prdBrand" autocomplete="off">
            </mat-form-field>
          </div>


          <div fxFlex="20" fxFlex.sm="100" fxFlex.xs="100">
            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label>UOM</mat-label>
              <input matInput formControlName="prdCasePack" autocomplete="off">
            </mat-form-field>
          </div>


          <!--<div fxFlex="30" fxFlex.sm="100" fxFlex.xs="100" class="margin-left stored_box">
            <mat-form-field id="chips"  appearance="outline" floatLabel="always">

              <mat-label>{{ "form.product.storedAreas" | translate }}</mat-label>

              <mat-chip-list #chipList aria-label="selection" formControlName="selectedAreas">

                <div *ngIf="checkedAreas?.length > 0; else emptyBlock">
                  <p *ngIf="checkedAreas?.length > 1" class="more__label">
                    <span *ngIf="0 > (checkedAreas.length - 4); else filledBlock">
                    </span>
                    <ng-template #filledBlock>+{{ checkedAreas.length - 3 }} {{ "more" | translate }}</ng-template></p>
                    <div class="mt-12">

                      <mat-chip *ngIf="checkedAreas[0].checked" selectable="true" removable="true" (removed)="onRemove(checkedAreas[0])">
                        <span [innerHTML]="checkedAreas[0].prdAreaNameEn"></span>
                        <mat-icon matChipRemove>cancel</mat-icon>
                      </mat-chip>

                      <mat-chip *ngIf="checkedAreas[1]?.checked" selectable="true" removable="true" (removed)="onRemove(checkedAreas[1])">
                        <span [innerHTML]="checkedAreas[1]?.prdAreaNameEn"></span>
                        <mat-icon matChipRemove>cancel</mat-icon>
                      </mat-chip>

                      <mat-chip *ngIf="checkedAreas[2]?.checked" selectable="true" removable="true" (removed)="onRemove(checkedAreas[2])">
                        <span [innerHTML]="checkedAreas[2]?.prdAreaNameEn"></span>
                        <mat-icon matChipRemove>cancel</mat-icon>
                      </mat-chip>

                    </div>

                </div>
                <ng-template #emptyBlock>
                  <mat-chip class="mt-15">
                    {{ "noStoredAreas" | translate }}
                  </mat-chip>
                </ng-template>
              </mat-chip-list>

            </mat-form-field>

            <div *ngIf="!matDialogData.finalized">
              <button type="button" class="add_stored" mat-button mat-raised-button color="accent" (click)="onOpenAreas()">{{ "storeInArea" | translate }}</button>
            </div>
          </div>-->

          <div fxFlex="30" fxFlex.sm="100" fxFlex.xs="100" class="margin-left stored_box" *ngIf="readStorageOption">

            <mat-form-field id="chips"  appearance="outline" floatLabel="always">

              <mat-label>{{ "storages" | translate }}</mat-label>

              <mat-chip-list #chipList aria-label="selection" formControlName="selectedStorages">

                <!-- Asset için tek storage göster -->
                <div *ngIf="form.get('prdIsAsset')?.value && checkedStorages?.length > 0">
                  <div class="mt-1">
                    <mat-chip
                      [selectable]="!matDialogData.finalized"
                      [removable]="!matDialogData.finalized"
                      (removed)="onStorageRemove(checkedStorages[0])" (onkeyup)="onStorageKeyUp($event)">
                      <span matTooltip="{{checkedStorages[0].path ? checkedStorages[0].path + ' -> ' : ''}}{{checkedStorages[0].item}}" matTooltipClass="tooltip">{{checkedStorages[0].item}}</span>
                      <mat-icon matChipRemove *ngIf="!matDialogData.finalized">cancel</mat-icon>
                    </mat-chip>
                  </div>
                </div>
                <!-- Normal ürün için çoklu storage -->
                <div *ngIf="!form.get('prdIsAsset')?.value && checkedStorages?.length > 0">
                    <div class="mt-1">
                          <div *ngFor="let storage of checkedStorages; let i = index">
                            <mat-chip
                            [selectable]="!matDialogData.finalized"
                            [removable]="!matDialogData.finalized"
                            (removed)="onStorageRemove(storage)" (onkeyup)="onStorageKeyUp($event)">
                              <span matTooltip="{{storage.path ? storage.path + ' -> ' : ''}}{{storage.item}}" matTooltipClass="tooltip">{{storage.item}}
                              </span>
                              <mat-icon matChipRemove *ngIf="!matDialogData.finalized">cancel</mat-icon>
                            </mat-chip>
                          </div>
                    </div>

                </div>
                <ng-template #emptyStorageBlock>
                  <mat-chip class="mt-1">
                    {{ "noStoredStorages" | translate }}
                  </mat-chip>
                </ng-template>
                <!-- Boş durum -->
                <ng-container *ngIf="checkedStorages?.length === 0">
                  <mat-chip class="mt-1">{{ "noStoredStorages" | translate }}</mat-chip>
                </ng-container>
              </mat-chip-list>


            </mat-form-field>
            <div *ngIf="!matDialogData.finalized">
              <button type="button" class="add_stored" mat-button mat-raised-button color="accent" (click)="onOpenStorages()">{{ "storeInStorage" | translate }}</button>
            </div>
          </div>


          <div fxFlex="30" fxFlex.sm="100" fxFlex.xs="100" class="serialpattern_edit_box margin-left" *ngIf="showSerialBlock">
            <mat-form-field  appearance="outline" floatLabel="always" [class.mat-form-field-invalid]="serialPatterns$?.length === 0 && form.controls['prdIsSerialized']?.value === true">
              <mat-label>{{ "products.table.serialnumberpatterns" | translate }}</mat-label>
              <mat-chip-list #chipList1 aria-label="SerialPatterns selection" class="product-edit__serialpatterns MatChipList" fxLayout="row" fxLayoutAlign="start start" [disabled]="!form.controls['prdIsSerialized']?.value || product$?.prdIsLockSerialized!=0">
                <mat-chip *ngFor="let b of serialPatterns$; let i = index"
                  [selectable]="!matDialogData.finalized"
                  [removable]="!matDialogData.finalized"
                  [ngClass]="b['selected'] == true ? 'MatChip--dark pointer' : 'pointer' " (removed)="onSerialPatternsDelete(i)" (click)="onSelectSerialPatterns(i)"
                  [attr.data-chipvalue]="i">
                  <span [innerHTML]="b.serialPattern"></span>
                  <button matChipRemove>
                    <mat-icon *ngIf="!matDialogData.finalized">cancel</mat-icon>
                  </button>
                </mat-chip>
                <input placeholder="{{ 'typeNewSerialPattern' | translate }}" [matChipInputFor]="chipList1" matChipInputAddOnBlur="true" id="chipInput1" #chipInput1
                  [matChipInputSeparatorKeyCodes]="separatorKeysCodes" (matChipInputTokenEnd)="onSerialPatternsSubmit($event)" (keydown)="onSerialPatternsKeyDown($event)" (keyup)="onSerialPatternsKeyUp($event)" [readonly]="matDialogData.finalized">
              </mat-chip-list>
            </mat-form-field>
            <mat-checkbox formControlName="prdIsSerialized" [disabled]="product$?.prdIsLockSerialized!=0 || product$?.serialLocked" [matTooltip]="getSerializedTooltipMessage()" matTooltipClass="tooltip">
              {{'text_is_serialized' | translate}}
            </mat-checkbox>
            <small *ngIf="product$?.serialLocked" class="product-edit__serialized-hint">{{'product.serializedLocked' | translate}}</small>
            <button mat-button mat-flat-button color="accent" (click)="goToSerialNumber()" *ngIf="serialPatterns$.length > 0 && showProductSerialNumberButton">
              {{'product.productSerialNumbers' | translate}}
            </button>
            <ng-container *ngIf="isSerialPatternsValid$ == false" class="validation-error">
              <mat-error>
                {{ serialPatternsError | translate }}
              </mat-error>
            </ng-container>
          </div>


        </div>

        <!-- Count Type Methods Box -->
        <div class="count-methods-box">
          <div class="count-methods-header" fxLayout="row" fxLayoutAlign="space-between center">
            <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="8px">
              <span class="count-methods-title">{{ "countTypeMethods" | translate }}</span>
              <mat-icon matTooltip="{{ 'tooltip.countMethod' | translate }}" matTooltipClass="tooltip" class="count-methods-info">info</mat-icon>
            </div>
            <button mat-icon-button class="count-methods-add-btn" type="button" (click)="addCount()" *ngIf="countingMethodsAddOption && !matDialogData.finalized">
              <mat-icon>add</mat-icon>
            </button>
          </div>
          <div class="count-methods-content">
            <div formArrayName="counts">
              <div *ngFor="let row of counts.controls; let i = index">
                <div [formGroupName]="i" class="product-edit__Counts">
                  <div fxLayout="row wrap" fxLayoutGap="12px" fxLayoutAlign="start center">
                    <div fxFlex="18" fxFlexAlign="center" class="text-left" fxFlex.lt-md="43">
                      <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ "description" | translate }}</mat-label>
                        <input matInput type="text" placeholder="{{ 'each' | translate }}" formControlName="pcmDesc" autocomplete="off"
                          onclick="this.select()" [readonly]="!countingMethodsDescriptionOption">
                      </mat-form-field>
                    </div>
                    <div fxFlex="10" fxFlexAlign="center" class="text-left" fxFlex.lt-md="43">
                      <mat-form-field appearance="outline" floatLabel="always"
                        [matTooltip]="form.get('prdIsAsset')?.value ? ('form.product.assetQuantityFixed' | translate) : ''"
                        matTooltipClass="tooltip">
                        <mat-label>{{ "quantity" | translate }}</mat-label>
                        <input placeholder="1" matInput type="text" formControlName="pcmItemQty" onclick="this.select()"
                          autocomplete="off" [readonly]="!countingMethodsQuantityOption || form.get('prdIsAsset')?.value">
                      </mat-form-field>
                    </div>
                    <div fxFlex="10" fxFlexAlign="center" class="text-left count-price-field" fxFlex.lt-md="43" [style.--lbl-ch]="('cost' | translate).length">
                      <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ "cost" | translate }}</mat-label>
                        <input matInput type="text" inputmode="decimal" placeholder="0.00" formControlName="pcmCost" (blur)="roundPrice(row.get('pcmCost'))"
                          onclick="this.select()" autocomplete="off">
<mat-error *ngIf="row.get('pcmCost')?.errors as e">{{ (e.priceMax ? 'products.price.max' : 'products.price.invalid') | translate: { max: priceMaxLabel } }}</mat-error>
                      </mat-form-field>
                    </div>
                    <div fxFlex="10" fxFlexAlign="center" class="text-left count-price-field" fxFlex.lt-md="43" [style.--lbl-ch]="('retail' | translate).length">
                      <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ "retail" | translate }}</mat-label>
                        <input matInput type="text" inputmode="decimal" placeholder="0.00" formControlName="pcmRetail" (blur)="roundPrice(row.get('pcmRetail'))"
                          onclick="this.select()" autocomplete="off">
<mat-error *ngIf="row.get('pcmRetail')?.errors as e">{{ (e.priceMax ? 'products.price.max' : 'products.price.invalid') | translate: { max: priceMaxLabel } }}</mat-error>
                      </mat-form-field>
                    </div>
                    <div fxFlex="10" fxFlexAlign="center" class="text-left" fxFlex.lt-md="43" [hidden]="!mls1Option">
                      <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ settings$.locMinQtyLabel1 ? settings$.locMinQtyLabel1 : "MOQ 1"}}</mat-label>
                        <input matInput type="number" placeholder="0" min="0" step="0.5" formControlName="pcmMinQty1"
                          onclick="this.select()" autocomplete="off">
                      </mat-form-field>
                    </div>
                    <div fxFlex="10" fxFlexAlign="center" class="text-left" fxFlex.lt-md="43"
                      matTooltip="Multiple minimum quantity support is disabled."
                      [matTooltipDisabled]="settings$?.locEnableMultiMinQty == 1" *ngIf="settings$?.locEnableMultiMinQty == 1" [hidden]="!mls2Option">
                      <mat-form-field appearance="outline" floatLabel="always">
                        <mat-label>{{ settings$.locMinQtyLabel2 ? settings$.locMinQtyLabel2 : "MOQ 2"}}</mat-label>
                        <input matInput type="number" placeholder="0" min="0" formControlName="pcmMinQty2"
                          onclick="this.select()" autocomplete="off">
                      </mat-form-field>
                    </div>
                    <div fxFlex="0 0 auto" class="text-center product-form-countBox" *ngIf="countingMethodsCountBy">
                      <mat-radio-group formControlName="pcmIsDefaultCountMethod" (change)="onToggleDefault(row, i)"
                        class="MatRadioButton--column-reverse">
                        <mat-radio-button [value]="1">
                          {{ "inputs.radio.count-by.text" | translate }}
                        </mat-radio-button>
                      </mat-radio-group>
                    </div>
                    <div fxFlex="0 0 auto" class="text-center product-form-countBox" *ngIf="countingMethodsOrderBy">
                      <mat-radio-group formControlName="pcmIsDefaultOrderMethod" (change)="onToggleOrder(row, i)"
                        class="MatRadioButton--column-reverse pl-30">
                        <mat-radio-button [value]="1">
                          {{ "inputs.radio.order-by.text" | translate }}
                        </mat-radio-button>
                      </mat-radio-group>
                    </div>
                    <div fxFlex class="product-form-close" fxLayoutAlign="end center">
                      <button [disabled]="i == 0" type="button" (click)="onRemoveCount(i, row)" class="count-methods-remove-btn" *ngIf="countingMethodsAddOption && !matDialogData.finalized">
                        <mat-icon>close</mat-icon>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Serial numbers in stock, grouped by storage (read only) -->
        <div class="serial-stock-card" *ngIf="locUsesSerializedModule === 1 && product$?.prdIsSerialized == 1 && product$?.prdID">
          <span class="count-methods-title">{{ 'serials.title' | translate }}</span>
          <app-serial-stock [prdID]="product$.prdID"></app-serial-stock>
        </div>

        <div hide fxLayout="row" fxFlexFill fxLayoutAlign="end center" class="bottom_update_btn">
          <button mat-raised-button fxFlexAlign="center" color="primary" (click)="onSubmit()" type="submit"
            [disabled]="!form.valid || (showSerialBlock && serialPatterns$?.length === 0 && form.controls['prdIsSerialized']?.value === true)" *ngIf="!matDialogData.finalized">{{ 'form.product.updateProduct' |
            translate }}</button>
        </div>

      </form>
    </div>
  </ng-container>
</ng-template>
<script type="text/javascript" defer>
  checkForDymoPrinter()
</script>
